import { useState } from 'react';
import { getMileageLogs, addMileageLog, getAnalysis } from '../../utils/storage';
import { useAuth } from '../../contexts/AuthContext';
import { saveMileageLog, updateVehicleMileage } from '../../services/serviceLogService';
import { reliableWrite } from '../../services/writeQueue';
import { formatDistance, distanceLabel, distanceLabelFull, milesToDisplay, displayToMiles } from '../../utils/units';

export default function MileageLog({ vehicle, onBack, onUpdateVehicle, onToast }) {
  const { user } = useAuth();
  const [logs, setLogs] = useState(() => getMileageLogs(vehicle.id));
  const [showAdd, setShowAdd] = useState(false);
  const [mileage, setMileage] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [tmu, setTmu] = useState(false);
  const [showDecreaseWarning, setShowDecreaseWarning] = useState(false);
  const [pendingEntry, setPendingEntry] = useState(null);

  const vu = vehicle.distance_units; // per-vehicle unit override
  const currentMileageDisplay = parseInt(milesToDisplay(vehicle.mileage || 0, vu)).toLocaleString();
  const unitLabel = distanceLabelFull(vu);
  const unitShort = distanceLabel(vu);

  function handleSubmit(e) {
    e.preventDefault();
    if (!mileage) return;

    // Convert from user's display unit to miles for storage
    const milesValue = mileage ? displayToMiles(parseInt(mileage, 10), vu) : null;
    const entry = {
      mileage: milesValue,
      date,
      notes: notes.trim(),
      tmu,
    };

    // Check for mileage decrease (compare in miles — internal unit)
    if (vehicle.mileage && milesValue < parseInt(vehicle.mileage, 10)) {
      setPendingEntry(entry);
      setShowDecreaseWarning(true);
      return;
    }

    submitEntry(entry);
  }

  function submitEntry(entry) {
    const updated = addMileageLog(vehicle.id, entry);
    setLogs(updated);

    // Update vehicle mileage and TMU flag
    if (entry.mileage) {
      onUpdateVehicle({ ...vehicle, mileage: entry.mileage, tmu: entry.tmu, lastMileageUpdateAt: new Date().toISOString() });
    }

    // Reliable Supabase saves (queues for retry on failure)
    if (user) {
      reliableWrite('mileageLog', { userId: user.id, vehicleId: vehicle.id, entry }, () =>
        saveMileageLog(user.id, vehicle.id, entry)
      );
      if (entry.mileage) {
        reliableWrite('vehicleMileage', { userId: user.id, vehicleId: vehicle.id, mileage: entry.mileage }, () =>
          updateVehicleMileage(user.id, vehicle.id, entry.mileage)
        );
      }
    }

    setShowAdd(false);
    setMileage('');
    setNotes('');
    setTmu(false);
    setShowDecreaseWarning(false);
    setPendingEntry(null);
    onToast?.('Mileage logged');

    // Suggest analysis refresh for significant mileage changes (>5000 mi)
    if (entry.mileage) {
      const existingAnalysis = getAnalysis(vehicle.id);
      if (existingAnalysis) {
        const analyzedMileage = parseInt(vehicle.mileage, 10) || 0;
        const newMileage = parseInt(entry.mileage, 10);
        if (Math.abs(newMileage - analyzedMileage) > 5000) {
          setTimeout(() => {
            onToast?.('Significant mileage change. Consider refreshing your vehicle analysis.');
          }, 1500);
        }
      }
    }

    // Suggest analysis refresh if mileage hasn't been updated in 60+ days
    if (entry.mileage && vehicle.lastMileageUpdateAt) {
      const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
      const lastUpdate = new Date(vehicle.lastMileageUpdateAt).getTime();
      if (Date.now() - lastUpdate > SIXTY_DAYS_MS) {
        const existingAnalysis = getAnalysis(vehicle.id);
        if (existingAnalysis) {
          setTimeout(() => {
            onToast?.('It\u2019s been a while! Consider refreshing your vehicle analysis.');
          }, 2000);
        }
      }
    }
  }

  function confirmDecrease() {
    if (pendingEntry) {
      submitEntry(pendingEntry);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="screen" style={{ overflowY: 'auto', paddingBottom: 100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div>
          <h1 className="screen-title" style={{ fontSize: '1.25rem' }}>Mileage Log</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
        </div>
      </div>

      {/* Current mileage display */}
      <div className="card" style={{ marginBottom: 20, textAlign: 'center' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
          Current Odometer
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 700, color: vehicle.tmu ? 'var(--warning)' : 'var(--text-primary)' }}>
          {currentMileageDisplay}
        </div>
        <div style={{ fontSize: '0.75rem', color: vehicle.tmu ? 'var(--warning)' : 'var(--text-muted)', marginTop: 4 }}>
          {vehicle.tmu ? `${unitLabel} (TMU)` : unitLabel}
        </div>
        {vehicle.tmu && (
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {`Total Mileage Unknown \u2014 may not reflect actual ${unitLabel} driven`}
          </div>
        )}
      </div>

      {/* Log Miles button / form */}
      {!showAdd ? (
        <button
          onClick={() => setShowAdd(true)}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '0.875rem',
            fontWeight: 600,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'var(--accent)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 20,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {unitLabel === 'kilometers' ? 'Log Kilometers' : 'Log Miles'}
        </button>
      ) : (
        <div
          className="card"
          style={{ marginBottom: 20, padding: 16 }}
        >
          <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 12 }}>{unitLabel === 'kilometers' ? 'Log Kilometers' : 'Log Miles'}</div>

          {/* Decrease warning modal */}
          {showDecreaseWarning && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--radius-sm)',
              padding: 14,
              marginBottom: 12,
            }}>
              <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--warning)', marginBottom: 6 }}>
                Mileage Decrease Detected
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.4 }}>
                The new reading ({parseInt(mileage, 10).toLocaleString()} {unitShort}) is lower than the current reading ({parseInt(milesToDisplay(vehicle.mileage, vu)).toLocaleString()} {unitShort}). This could indicate a TMU situation or data entry error.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setShowDecreaseWarning(false); setPendingEntry(null); }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '0.8125rem',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDecrease}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'var(--warning)',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Log Anyway
                </button>
              </div>
            </div>
          )}

          {!showDecreaseWarning && (
            <form onSubmit={handleSubmit}>
              {/* Odometer reading — always visible */}
              <div style={{ marginBottom: 10 }}>
                <label style={miniLabelStyle}>Current Odometer Reading</label>
                <input
                  className="form-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 45000"
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value.replace(/[^0-9]/g, ''))}
                  style={miniInputStyle}
                  required
                  autoFocus
                />
              </div>

              {/* TMU flag checkbox */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  cursor: 'pointer',
                  marginBottom: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={tmu}
                  onChange={(e) => setTmu(e.target.checked)}
                  style={{
                    width: 16,
                    height: 16,
                    accentColor: 'var(--warning)',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                />
                <div>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: tmu ? 'var(--warning)' : 'var(--text-secondary)' }}>
                    Total Mileage Unknown (TMU)
                  </span>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    Check if odometer reading may not reflect true total mileage
                  </div>
                </div>
              </label>

              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={miniLabelStyle}>Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={{ ...miniInputStyle, colorScheme: 'dark' }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={miniLabelStyle}>Notes (optional)</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="e.g. Road trip, routine check"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={miniInputStyle}
                />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setShowAdd(false); setTmu(false); setMileage(''); setNotes(''); }}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    fontSize: '0.8125rem',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'var(--accent)',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Mileage log entries */}
      <div>
        <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          History
        </h2>
        {logs.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            No mileage entries yet.
          </div>
        ) : (
          <div className="card" style={{ padding: '4px 16px' }}>
            {logs.map((log, i) => {
              const prevLog = logs[i + 1]; // logs are newest-first
              const milesDriven = log.mileage && prevLog?.mileage ? log.mileage - prevLog.mileage : null;
              return (
                <div
                  key={log.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '12px 0',
                    borderBottom: i < logs.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: log.tmu ? 'var(--warning)' : 'var(--text-primary)' }}>
                        {log.mileage ? formatDistance(log.mileage, vu) : '—'}{log.tmu ? ' (TMU)' : ''}
                      </span>
                      {milesDriven != null && milesDriven > 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          +{parseInt(milesToDisplay(milesDriven, vu)).toLocaleString()} {unitShort}
                        </span>
                      )}
                      {milesDriven != null && milesDriven < 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--warning)' }}>
                          {parseInt(milesToDisplay(milesDriven, vu)).toLocaleString()} {unitShort}
                        </span>
                      )}
                    </div>
                    {log.notes && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.3 }}>
                        {log.notes}
                      </p>
                    )}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                    {formatDate(log.date)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const miniLabelStyle = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

const miniInputStyle = {
  fontSize: '0.8125rem',
  padding: '8px 10px',
};
